<!-- eslint-disable max-lines -->
<script setup lang="ts">
import { queryBuildingDaoApi } from '@/accessors/api/requests/disasterCommonBuildingApiGwFuncs'
import {
  convertToFrontendErrorCode,
  ViewCode,
  ErrorCode
} from '@/constants/error/convertToFrontendErrorCode'
import { getAllTileUrls, downloadTileImage } from '@/libs/leaflet/tileManager'
import {
  type CustomFeature,
  setDownloadedFeature,
  getDownloadedDistrictId,
  calcRectangularArea
} from '@/libs/leaflet/featureManager'
import { signout } from '@/modules/miiaFw/MiiaFwCognito'
import { useHoldTemporaryStore } from '@/stores/holdTemporaryStore'
import { useOfflineDataDownloadProcessStore } from '@/stores/offlineDataDownloadProcessStore'
import { latLng } from 'leaflet'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import {
  set as setMapTile,
  type tileStoreData,
  clear as leafletDataClear
} from '@/libs/leaflet/indexedDB'
import {
  set as indexedDBSet,
  clear as apiDataClear
} from '@/accessors/indexedDB/common/indexedDBController'
import L from 'leaflet'
import { useAdminDistrictsStore } from '@/stores/adminDistrictsStore'
import type { QueryBuildingRequestParam, QueryBuildingResult } from '@/types/accessor/api/building'
import CommonModal from '@/components/common/dialog/CommonModal.vue'
import { useCommonModalStore } from '@/stores/commonModalStore'

const props = defineProps({
  map: { type: L.Map }
})

const holdTemporaryStore = useHoldTemporaryStore()
const offlineDataDownloadProcessStore = useOfflineDataDownloadProcessStore()
const adminDistrictsStore = useAdminDistrictsStore()
const commonModalStore = useCommonModalStore()

const showModal = ref<boolean>(false)
// ダウンロード実行フラグ
const isLoading = ref<boolean>(false)
// 地図画像のダウンロードState
const currentDownloadedTileImageCount = ref<number>(0)
const totalDownloadTileImageCount = ref<number>(0)
// 建物のダウンロードState
const totalDownloadBuildingCount = ref<number>(0)
// ダウンロード失敗フラグ
const isFailureTileImage = ref<boolean>(false)
const isFailureDownloadBuilding = ref<boolean>(false)
const downloadedTileImage = ref<string[]>([])
const downloadedBuildings = ref<string[]>([])
const fetchedBuildingApiCount = ref<number>(0)
// タスク中断フラグ
let isTaskAborted = false
/** 更新処理フラグ */
let isUpdateProcess = false

const router = useRouter()
const deviceInfo = holdTemporaryStore.deviceInfo
// 選択した地域のstreetCodeを格納する配列。地域の選択時に直接streetCodeを取ってこれないので処理して格納する
const selectedStreetCodeList = ref<string[]>([])

// ダウンロード中断モーダルを開く
const openConfirmSuspendModal = () => {
  commonModalStore
    .openConfirmModal({
      title: 'ダウンロードの中断',
      body: isUpdateProcess
        ? 'ダウンロード処理を中断します。<br>よろしいですか?<br><br>※更新前の地図は削除されているため、キャンセルしてもオフライン地図を利用できません。利用する場合は再度地図をダウンロードしてください。'
        : 'ダウンロード処理を中断します。<br>よろしいですか?',
      okTitle: 'はい',
      cancelTitle: 'いいえ'
    })
    .then(() => {
      isTaskAborted = true
    })
}

// ダウンロード処理の実行
const executeDownLoadMapEntity = async (_isUpdateProcess: boolean = false) => {
  isUpdateProcess = _isUpdateProcess
  isLoading.value = true
  await initData()
  // ダウンロード対象の地区を取得
  const targetFeatures = offlineDataDownloadProcessStore.getTargetDownloadFeature
  // ダウンロード件数を計算する
  await calcDataAmount(targetFeatures)

  let errMsg = ''
  // 先にStreetCodeの配列作成を行う。
  for (const f of targetFeatures) {
    generateStreetCode(f)
  }
  // StreetCodeの配列の作成が完了したら、建物情報の取得を行う。
  if (!isTaskAborted) {
    await downLoadBuilding().catch((e: Error) => {
      console.error(e)
      errMsg = e.message
    })
  }
  // 画像タイルの取得を行う。
  for (const f of targetFeatures) {
    if (!isTaskAborted) {
      let errFlg = false
      await downLoadFeatureMapTiles(f)
        .then(async () => {
          await setDownloadedFeature(f)
        })
        .catch((e: Error) => {
          console.error(e)
          errMsg = e.message
          errFlg = true
        })
      if (errFlg) break
    }
  }
  if (isFailureTileImage.value) {
    commonModalStore
      .openAlertModal({
        body: `地図画像のダウンロードに失敗しました。<br>${errMsg}`,
        title: 'ダウンロードに失敗しました',
        okTitle: '閉じる'
      })
      .then(() => {
        isLoading.value = false
        window.localStorage.setItem('availableOfflineMap', '0')
        closeModal()
      })
  } else if (isFailureDownloadBuilding.value) {
    commonModalStore
      .openAlertModal({
        body: `建物情報のダウンロードに失敗しました。<br>${errMsg}`,
        title: 'ダウンロードに失敗しました',
        okTitle: '閉じる'
      })
      .then(() => {
        isLoading.value = false
        window.localStorage.setItem('availableOfflineMap', '0')
        closeModal()
      })
  } else if (isTaskAborted) {
    commonModalStore
      .openAlertModal({
        body: 'ダウンロードを中断しました。',
        title: 'ダウンロードを中断しました',
        okTitle: '閉じる'
      })
      .then(async () => {
        // 1地区でもダウンロード出来ていればオフライン地図を利用できる
        const downloadedFeatures = await getDownloadedDistrictId()
        window.localStorage.setItem(
          'availableOfflineMap',
          downloadedFeatures.length === 0 ? '0' : '1'
        )
        isLoading.value = false
        closeModal()
      })
  } else {
    commonModalStore
      .openAlertModal({
        body: '正常にダウンロード処理が完了しました。',
        title: 'ダウンロードが完了しました',
        okTitle: '閉じる'
      })
      .then(() => {
        isLoading.value = false
        offlineDataDownloadProcessStore.updateIsDownloaded(true)
        window.localStorage.setItem('availableOfflineMap', '1')
        closeModal()
      })
  }
}

// ダウンロード実行前の初期化処理
const initData = async () => {
  currentDownloadedTileImageCount.value = 0
  totalDownloadBuildingCount.value = 0
  fetchedBuildingApiCount.value = 0
  totalDownloadTileImageCount.value = 0
  isTaskAborted = false
  isFailureTileImage.value = false
  isFailureDownloadBuilding.value = false
  downloadedTileImage.value = []
  downloadedBuildings.value = []
  selectedStreetCodeList.value = []
  // 更新の場合、既存データは削除する（削除しておかないと、"削除"の更新を反映できないため）
  if (isUpdateProcess) {
    await clearIndexedDB()
  }
  window.localStorage.setItem('availableOfflineMap', '0')
}
// indexedDBの初期化
const clearIndexedDB = async () => {
  await leafletDataClear('tileStore')
  await leafletDataClear('downloadedFeature')
  await apiDataClear('building')
}
// ダウンロード予定のデータ件数を設定する
const calcDataAmount = async (targetDownloadFeature: CustomFeature[]): Promise<void> => {
  // ダウンロード対象のタイル総件数を計算
  const data: string[] = []
  for (const f of targetDownloadFeature) {
    const area = calcRectangularArea(f)
    const tileUrls = getAllTileUrls(
      props.map as L.Map,
      latLng(area.swCoordinates[1], area.swCoordinates[0]),
      latLng(area.neCoordinates[1], area.neCoordinates[0]),
      18
    )
    data.push(...tileUrls)
  }
  // 地図タイルが複数地区で重複する場合があるので、単一となるようにフィルター
  const targetDownLoadTiles = Array.from(new Set(data))
  totalDownloadTileImageCount.value = targetDownLoadTiles.length
}

// 地図画像のタイルをダウンロードし、indexedDBに保存する
const downLoadFeatureMapTiles = async (feature: CustomFeature): Promise<void> => {
  const _sleep = (ms: number | undefined) => new Promise(resolve => setTimeout(resolve, ms))
  const area = calcRectangularArea(feature)
  const map = props.map as L.Map
  const tileUrls = getAllTileUrls(
    map,
    latLng(area.swCoordinates[1], area.swCoordinates[0]),
    latLng(area.neCoordinates[1], area.neCoordinates[0]),
    18
  )

  for (let i = 0; i < tileUrls.length; i++) {
    if (isTaskAborted) {
      throw new Error('処理が中断されました')
    }
    if (downloadedTileImage.value.includes(tileUrls[i])) {
      continue
    }
    // リクエスト過多防止
    if (currentDownloadedTileImageCount.value % 100 === 0) {
      await _sleep(3000)
    }
    // 画面描画が追いつかなくなるためsleepする
    const res = await downloadTileImage(tileUrls[i]).catch(e => {
      console.error(e)
      isFailureTileImage.value = true
      throw new Error('通信状態を確認して再度実行してください。')
    })
    await setMapTile<tileStoreData>('tileStore', res.key, res)
      .then(() => {
        currentDownloadedTileImageCount.value++
        downloadedTileImage.value.push(tileUrls[i])
      })
      .catch(e => {
        console.error(e)
        isFailureTileImage.value = true
        throw new Error(
          '地図画像を保存できませんでした。<br>ストレージの空き容量を確認してください。'
        )
      })
  }
}
// 選択した地域の建物情報streetCodeでクエリをかけるために、選択した地域とstreetNameを確認して配列に追加していく。
const generateStreetCode = (feature: CustomFeature) => {
  const adminDistrictsStreetList = adminDistrictsStore.getStreetList
  for (const item of adminDistrictsStreetList) {
    if (
      feature.properties.streetName === item.text &&
      !selectedStreetCodeList.value.includes(item.value)
    ) {
      selectedStreetCodeList.value = selectedStreetCodeList.value.concat(item.value)
    }
  }
}
// 地区内の建物をダウンロードし、indexedDBに保存する
const downLoadBuilding = async (): Promise<void> => {
  // streetCodeの値ごとにクエリをかける
  for (const f of selectedStreetCodeList.value) {
    if (isTaskAborted) {
      throw new Error('処理が中断されました')
    }
    const buildingsApiQueryParams = {
      streetCode: f,
      // 指定しないとStreetCodeが取得されない
      includingPersonalInformation: true
    } as QueryBuildingRequestParam
    // 中断処理を考慮してindexedDB保存処理をaccessDataBasedOnNetworkにラップしない
    const res = await queryBuildingDaoApi(buildingsApiQueryParams).catch(async e => {
      console.error(e)
      isFailureDownloadBuilding.value = true
      return await new Promise(resolve => {
        const frontendErrorCode = convertToFrontendErrorCode(
          ViewCode.INSPECTION_PLAN,
          String(e.message.errorCode)
        )
        if (frontendErrorCode === ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_ERROR) {
          commonModalStore
            .openConfirmModal({
              title: '未登録のデバイスです',
              body: `本アプリを利用するデバイスとして登録されていません。<br>
              デバイス登録から利用しているデバイスの登録を実施してください。`,
              okTitle: '閉じる'
            })
            .then(() => {
              signout()
              router.push({ path: '/' })
              resolve(null)
            })
        } else if (
          frontendErrorCode === ErrorCode.INSPECTION_PLAN_DEVICE_AUTH_NO_PERMISSION_ERROR
        ) {
          commonModalStore
            .openConfirmModal({
              title: 'アクセスが禁止されています',
              body: `使用されているデバイスはアクセスが禁止されています。<br>
              管理者にお問い合わせください。<br><br>
              \u3000デバイスID: ${deviceInfo.deviceId}<br>
              デバイス名称: ${deviceInfo.deviceName}<br>
              アクセス許可: 禁止`,
              okTitle: '閉じる'
            })
            .then(() => {
              signout()
              router.push({ path: '/' })
              resolve(null)
            })
        }
        throw new Error('建物情報を取得できませんでした')
      })
    })
    if (res) {
      const buildings = res as QueryBuildingResult[]
      const filteredBuildings = buildings.filter(d => {
        return !downloadedBuildings.value.includes(d.buildingId)
      })
      totalDownloadBuildingCount.value = totalDownloadBuildingCount.value + filteredBuildings.length
      for (const building of filteredBuildings) {
        if (isTaskAborted) {
          throw new Error('処理が中断されました')
        }
        await indexedDBSet('building', building, 'get', 'fetched', building.buildingId)
          .then(() => {
            downloadedBuildings.value.push(building.buildingId)
          })
          .catch(e => {
            console.error(e)
            isFailureDownloadBuilding.value = true
            throw new Error(
              '建物情報を保存できませんでした。<br>ストレージの空き容量を確認してください。'
            )
          })
      }
      fetchedBuildingApiCount.value++
    }
  }
}

const openModal = () => {
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
}
// ライフサイクル
onMounted(async () => {})
onBeforeUnmount(() => {})
defineExpose({
  showModal,
  isLoading,
  downloadedTileImage,
  downloadedBuildings,
  totalDownloadTileImageCount,
  totalDownloadBuildingCount,
  isFailureTileImage,
  isFailureDownloadBuilding,
  fetchedBuildingApiCount,
  deviceInfo,
  selectedStreetCodeList,
  openModal,
  closeModal,
  clearIndexedDB,
  downLoadFeatureMapTiles,
  openConfirmSuspendModal,
  executeDownLoadMapEntity
})
</script>

<template>
  <div>
    <b-modal
      no-close-on-backdrop
      centered
      stacking
      class=""
      v-model="showModal"
      ref="modal"
      size="lg"
      footer-bg-variant="white"
      :no-enforce-focus="true"
    >
      <!-- ヘッダー部 -->
      <template #header>
        <div class="w-100">
          <div class="d-flex flex-row bd-highlight justify-content-center">
            <p class="h3 mt-3">地図データのダウンロード</p>
          </div>
        </div>
      </template>
      <b-card class="m-0" border-variant="white" :style="{ height: '400px' }">
        <b-container>
          <b-row class="text-left mt-3">
            <b-col sm="2" md="2" align-self="center">
              <div
                v-if="
                  downloadedTileImage.length > 0 &&
                  downloadedTileImage.length === totalDownloadTileImageCount
                "
                class="state"
              >
                <b-icon class="ml-2" icon="check" scale="3" variant="success"></b-icon>
              </div>
              <div v-else-if="isFailureTileImage" class="state">
                <b-icon
                  class="ml-2"
                  icon="exclamation-circle"
                  scale="1.5"
                  variant="danger"
                ></b-icon>
              </div>
              <!-- b-spinnerが原因不明で利用できないためloaderを自作 -->
              <div v-else-if="isLoading && !isFailureTileImage" class="state">
                <span class="loader"></span>
              </div>
              <div v-else><b-icon class="ml-2" icon="dash-lg" scale="2"></b-icon></div>
            </b-col>
            <b-col sm="4" md="4" align-self="center">地図画像</b-col>
            <b-col sm="auto" md="auto" align-self="center"
              >({{ downloadedTileImage.length }}/{{ totalDownloadTileImageCount }}件)</b-col
            >
          </b-row>
          <b-row class="text-left mt-3r">
            <b-col sm="2" md="2" align-self="center">
              <div
                v-if="
                  fetchedBuildingApiCount > 0 &&
                  fetchedBuildingApiCount === selectedStreetCodeList.length
                "
                class="state"
              >
                <b-icon class="ml-2" icon="check" scale="3" variant="success"></b-icon>
              </div>
              <div v-else-if="isFailureDownloadBuilding" class="state">
                <b-icon
                  class="ml-2"
                  icon="exclamation-circle"
                  scale="1.5"
                  variant="danger"
                ></b-icon>
              </div>
              <div v-else-if="isLoading && !isFailureDownloadBuilding" class="state">
                <span class="loader pb-2"></span>
              </div>
              <div v-else><b-icon class="ml-2" icon="dash-lg" scale="1"></b-icon></div>
            </b-col>
            <b-col sm="4" md="4" align-self="center">建物データ </b-col>
            <b-col sm="auto" md="auto" align-self="center"
              >({{ downloadedBuildings.length }}/{{ totalDownloadBuildingCount }}件)
            </b-col>
          </b-row>
        </b-container>
      </b-card>
      <!-- フッター部 -->
      <template #footer>
        <div class="w-100">
          <div class="d-flex flex-row justify-content-end">
            <!-- ダウンロード前に確認モーダルを表示する -->
            <b-button size="lg" class="mx-2" variant="secondary" @click="openConfirmSuspendModal">
              キャンセル
            </b-button>
          </div>
        </div>
      </template>
    </b-modal>
    <CommonModal />
  </div>
</template>

<style scoped>
.state {
  width: 36px;
  height: 36px;
}

/* ローディングスピナー */
.loader {
  width: 30px;
  height: 30px;
  border: 5px solid #b3b3b3;
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* ダイアログのフルスクリーン表示用 */
:deep(.modal-lg) {
  width: 90%;
  max-width: 100%;
}
</style>
